'use client';

import * as React from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { MessageSquare, RefreshCw, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { SectionHeading } from '@/components/frontend/home/section-heading';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { getLocalizedPath } from '@/lib/locale-utils';
import { cn } from '@/lib/utils';

export function MLMServices() {
    const locale = useLocale();
    const t = useTranslations('home.mlmServices');
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    const content = {
        pillLabel: t('pillLabel'),
        title: t('title'),
        subtitle: t('subtitle'),
        buttonText: t('buttonText'),
    };

    type ServiceTab = {
        id: string;
        title: string;
        description: string;
        href: string;
        icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
        iconColor: string;
    };

    const tabs: ServiceTab[] = React.useMemo(() => {
        const rawTabs = t.raw('tabs');
        const baseTabs = Array.isArray(rawTabs)
            ? (rawTabs as { id: string; title: string; description: string; href: string }[])
            : [];
        const icons = [MessageSquare, RefreshCw, Calculator];
        const iconColors = [
            'text-blue-600 dark:text-blue-400',
            'text-purple-600 dark:text-purple-400',
            'text-green-600 dark:text-green-400',
        ];

        return baseTabs.map((tab, index) => ({
            ...tab,
            icon: icons[index] ?? MessageSquare,
            iconColor: iconColors[index] ?? 'text-gray-600 dark:text-gray-400',
        }));
    }, [t]);

    const [activeTab, setActiveTab] = React.useState(() => tabs[0]?.id || '');
    const [isTransitioning, setIsTransitioning] = React.useState(false);

    React.useEffect(() => {
        if (tabs.length && !tabs.find((tab) => tab.id === activeTab)) {
            setActiveTab(tabs[0].id);
        }
    }, [tabs, activeTab]);

    const activeTabData = tabs.find((tab) => tab.id === activeTab) || tabs[0];

    // Don't render if no tabs are available
    if (!tabs.length || !activeTabData) {
        return null;
    }

    const handleTabChange = (tabId: string) => {
        if (tabId === activeTab || isTransitioning) return;

        setIsTransitioning(true);
        setTimeout(() => {
            setActiveTab(tabId);
            setTimeout(() => {
                setIsTransitioning(false);
            }, 100);
        }, 150);
    };

    return (
        <SectionWrapper
            ref={sectionRef.ref}
            variant="gradient"
            padding="md"
            className="relative"
        >
            {/* Background accents */}
            <div className="pointer-events-none absolute inset-0 opacity-40">
                <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-primary-500/5 blur-3xl" />
                <div className="absolute right-1/4 bottom-0 h-96 w-96 rounded-full bg-blue-500/5 blur-3xl" />
            </div>

            <div className="relative z-10">
                {/* Header */}
                <SectionHeading
                    pill={content.pillLabel}
                    title={content.title}
                    subtitle={content.subtitle}
                    isVisible={sectionRef.isVisible}
                />

                <div className="relative max-w-4xl mx-auto rounded-xl p-4 md:p-10 bg-white/50 dark:bg-gray-900/30">
                    {/* Horizontal Tab Buttons - Center Aligned, slightly overlapping border */}
                    <div className="flex flex-wrap justify-center gap-4 mb-6">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;

                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabChange(tab.id)}
                                    className={cn(
                                        'flex items-center gap-3 px-6 py-3 rounded-lg  transition-all duration-300 transform',
                                        isActive
                                            ? 'bg-primary-600 text-white  hover:bg-primary-700 scale-105 shadow-lg shadow-primary-500/30'
                                            : 'bg-gray-100 text-gray-900  hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700  hover:scale-105'
                                    )}
                                >
                                    <Icon
                                        className={cn(
                                            'h-5 w-5 flex-shrink-0',
                                            isActive ? 'text-white' : tab.iconColor || 'text-gray-600 dark:text-gray-400'
                                        )}
                                    />
                                    <span className="text-sm font-medium whitespace-nowrap">{tab.title}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Content Section - Center Aligned, Full Width */}
                    <div className="relative min-h-[200px] text-center pt-6">
                        <div
                            key={activeTab}
                            className={cn(
                                'transition-all duration-500 ease-in-out',
                                isTransitioning
                                    ? 'opacity-0 translate-y-4 scale-95'
                                    : 'opacity-100 translate-y-0 scale-100'
                            )}
                        >
                            <Typography
                                variant="h3"
                                className="mb-4 transition-all duration-300"
                            >
                                {activeTabData.title}
                            </Typography>
                            <Typography variant="p" className="mb-8 text-gray-600 dark:text-gray-300 text-base leading-relaxed transition-all duration-300">
                                {activeTabData.description}
                            </Typography>
                            <div className="transition-all duration-300 transform">
                                {/* <Button
                                    href={getLocalizedPath(activeTabData.href, locale)}
                                    variant="primary"
                                    size="md"
                                >
                                    {content.buttonText}
                                </Button> */}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}

